import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spin,
  message,
  Button,
  Space,
  Tag,
  Image,
  DatePicker,
  Popconfirm,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TournamentRegistrations = () => {
  /* -------------------- STATE -------------------- */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  /* -------------------- FETCH DATA -------------------- */
  const fetchRegistrations = async (pageNo, size, from, to) => {
    setLoading(true);
    try {
      const res = await api.get("/tour-player-registrations/", {
        params: {
          page: pageNo,
          pageSize: size,
          fromDate: from || undefined,
          toDate: to || undefined,
        },
      });

      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(pageNo);
      setPageSize(size);
    } catch (err) {
      message.error("Failed to load registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations(1, pageSize, null, null);
    // eslint-disable-next-line
  }, []);

  /* -------------------- STATUS UPDATE -------------------- */
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/tour-player-registrations/${id}/status`, { status });
      message.success(`Status updated to ${status}`);
      fetchRegistrations(page, pageSize, fromDate, toDate);
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  /* -------------------- DELETE -------------------- */
  const deleteRegistration = async (id) => {
    try {
      await api.delete(`/tour-player-registrations/${id}`);
      message.success("Registration deleted");
      fetchRegistrations(page, pageSize, fromDate, toDate);
    } catch (err) {
      message.error("Failed to delete registration");
    }
  };

  /* -------------------- EXPORTS -------------------- */
const exportTournamentExcel = (tournamentId, tournamentName) => {
  const filteredData = data.filter(
    (item) => item.tournament_id === tournamentId
  );

  if (!filteredData.length) {
    message.warning("No registrations found for this tournament.");
    return;
  }

  const sheetData = filteredData.map((item, index) => ({
    "S.No": index + 1,
    "Profile Photo": item.profile_photo || "",
    Name: item.name_as_aadhaar,
    Role: item.role,
    Mobile: item.mobile,
    Email: item.email,
    DOB: item.dob ? dayjs(item.dob).format("DD-MM-YYYY") : "",
    Age: item.dob ? dayjs().diff(dayjs(item.dob), "year") : "",
    Status: item.status,
    Location: item.location || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Registrations"
  );

  XLSX.writeFile(
    workbook,
    `${tournamentName}.xlsx`
  );
};

 
 
const exportTournamentPDF = async (tournamentId, tournamentName) => {
  const filteredData = data.filter(
    (item) => item.tournament_id === tournamentId
  );

  if (!filteredData.length) {
    alert("No registrations found for this tournament.");
    return;
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "A4",
  });

  // Title
  doc.setFontSize(18);
  doc.text(tournamentName, 40, 40);

  doc.setFontSize(12);
  doc.text("Tournament Registrations", 40, 60);

  autoTable(doc, {
    startY: 80,
    head: [[
      "S.No",
      "Name",
      "Role",
      "Mobile",
      "Email",
      "DOB",
      "Age",
      "Status", 
      "Location",
    ]],

    body: filteredData.map((item, index) => [
      index + 1,
      item.name_as_aadhaar,
      item.role,
      item.mobile,
      item.email,
      item.dob ? dayjs(item.dob).format("DD-MM-YYYY") : "",
      item.dob ? dayjs().diff(dayjs(item.dob), "year") : "",
      item.status,
      item.location || "",
    ]),

    styles: {
      fontSize: 9,
    },

    headStyles: {
      fillColor: [22, 160, 133],
    },
  });

  doc.save(`${tournamentName}.pdf`);
};


  /* -------------------- TABLE COLUMNS -------------------- */
  const columns = [
    {
      title: "Player",
      render: (_, r) => (
        <Space>
          <Image
            width={40}
            height={40}
            style={{ borderRadius: "50%" }}
            src={r.profile_photo}
          />
          <div>
            <strong>{r.name_as_aadhaar}</strong>
            <br />
            <small>{r.role}</small>
          </div>
        </Space>
      ),
    },
    {
      title: "Contact",
      responsive: ["md"],
      render: (_, r) => (
        <>
          <div>{r.mobile}</div>
          <small>{r.email}</small>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={s === "pending" ? "orange" : s === "approved" ? "green" : "red"}>
          {s.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "UTR Number",
      dataIndex: "utr_number",
      render: (utr) => (
        <Tag color="blue">{utr}</Tag>
      ),
    },
    {
    title: "Payment",
    responsive: ["md"],
        render: (_, r) => (
        <Image
            width={60}
            src={r.payment_screenshot}
            style={{ borderRadius: 6 }}
            fallback="https://via.placeholder.com/60"
            preview={{
            mask: "View",
            }}
        />
        ),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<CheckOutlined />}
            size="small"
            type="primary"
            disabled={r.status !== "pending"}
            onClick={() => updateStatus(r.id, "approved")}
          />

          <Button
            icon={<CloseOutlined />}
            size="small"
            danger
            disabled={r.status !== "pending"}
            onClick={() => updateStatus(r.id, "rejected")}
          />

          <Popconfirm
            title="Delete this registration?"
            onConfirm={() => deleteRegistration(r.id)}
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* -------------------- LOADING -------------------- */
  if (loading && !data.length) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  /* -------------------- RENDER -------------------- */
  return (
    <div>
      <h2>Tournament Registrations</h2>

      <Card
        extra={
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              onClick={() =>
                exportTournamentExcel(
                  1,
                  "Chhindwara Premier League 2026"
                )
              }
            >
              CPL 2026 Excel
            </Button>

            <Button
              icon={<FileExcelOutlined />}
              onClick={() =>
                exportTournamentExcel(
                  2,
                  "Chhindwara Premier League 2026 (Under 19)"
                )
              }
            >
              CPL 2026 U19 Excel
            </Button>

            <Button icon={<FilePdfOutlined />} danger onClick={  () => exportTournamentPDF(1, "Chhindwara Premier League 2026")}>
              Download CPL 2026 PDF
            </Button>
             <Button icon={<FilePdfOutlined />} danger onClick={  () => exportTournamentPDF(2, "Chhindwara Premier League 2026 (Under 19)")}>
              Download CPL 2026 U19 PDF
            </Button>
          </Space>
        }
      >
        {/* DATE FILTERS */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            onClick={() => {
              const t = dayjs().format("YYYY-MM-DD");
              setFromDate(t);
              setToDate(t);
              fetchRegistrations(1, pageSize, t, t);
            }}
          >
            Today
          </Button>

          <Button
            onClick={() => {
              setFromDate(null);
              setToDate(null);
              fetchRegistrations(1, pageSize, null, null);
            }}
          >
            All
          </Button>

          <DatePicker.RangePicker
            onChange={(d) => {
              if (!d) {
                fetchRegistrations(1, pageSize, null, null);
                return;
              }
              fetchRegistrations(
                1,
                pageSize,
                d[0].format("YYYY-MM-DD"),
                d[1].format("YYYY-MM-DD")
              );
            }}
          />
        </Space>

        {/* TABLE */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true, // enables dropdown
            pageSizeOptions: ["10", "50", "100", "1000"], // allowed options
            showTotal: (total) => `Total ${total} registrations`,
            onChange: (p, ps) => {
              fetchRegistrations(p, ps, fromDate, toDate);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default TournamentRegistrations;
